// services/TokenUsageService.js
import { tokenUsageCachedAxios, noCacheAxios } from '../../../shared/config/cachedAxiosConfig';
import { TOKEN_USAGE_ENDPOINTS } from '../config/tokenUsageEndpoints';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import logger from '../../../shared/services/logging/logger';

/**
 * Helper function to build common headers
 */
const buildHeaders = async (userData) => {
  const baseHeaders = await getAuthHeaders();
  return {
    ...baseHeaders,
    'X-Tenant-Id': userData.organizationId || '',
    'X-User-Id': userData.userId || '',
    'X-Bucket-Name': userData.fileManagementBucket || ''
  };
};

export const TokenUsageService = {
  /**
   * Get metrics overview with total cost, limit status, top users
   */
  getOverview: async (userData, startDate = null, endDate = null) => {
    try {
      const headers = await buildHeaders(userData);

      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      logger.info('Fetching token usage overview', { url: TOKEN_USAGE_ENDPOINTS.overview, params, tenantId: userData.organizationId });

      const response = await tokenUsageCachedAxios.get(TOKEN_USAGE_ENDPOINTS.overview, { headers, params });

      logger.debug('Overview data received', { dataSize: response.data ? Object.keys(response.data).length : 0 });

      return response.data;
    } catch (error) {
      logger.error('Error fetching overview', { error: error.message, status: error.response?.status, data: error.response?.data });

      const enhancedError = new Error(`Failed to fetch token usage overview: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        tenantId: userData.organizationId,
        userId: userData.userId,
        endpoint: TOKEN_USAGE_ENDPOINTS.overview
      };

      throw enhancedError;
    }
  },

  /**
   * Get trends data for charts (daily/hourly aggregation)
   */
  getTrends: async (userData, startDate = null, endDate = null, groupBy = 'day', userId = null) => {
    try {
      const headers = await buildHeaders(userData);

      const params = { groupBy };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (userId) params.userId = userId;

      logger.info('Fetching trends', { url: TOKEN_USAGE_ENDPOINTS.trends, params });

      const response = await tokenUsageCachedAxios.get(TOKEN_USAGE_ENDPOINTS.trends, { headers, params });

      logger.debug('Trends data received', { dataPoints: response.data?.length });

      return response.data;
    } catch (error) {
      logger.error('Error fetching trends', { error: error.message });

      const enhancedError = new Error(`Failed to fetch trends: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        tenantId: userData.organizationId,
        userId: userData.userId,
        endpoint: TOKEN_USAGE_ENDPOINTS.trends
      };

      throw enhancedError;
    }
  },

  /**
   * Get list of all users with their usage summary
   */
  getUsers: async (userData, startDate = null, endDate = null, sortBy = 'cost', limit = null) => {
    try {
      const headers = await buildHeaders(userData);

      const params = { sortBy };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (limit) params.limit = limit;

      logger.info('Fetching users list', { url: TOKEN_USAGE_ENDPOINTS.users, params });

      const response = await tokenUsageCachedAxios.get(TOKEN_USAGE_ENDPOINTS.users, { headers, params });

      logger.debug('Users data received', { userCount: response.data?.length });

      return response.data;
    } catch (error) {
      logger.error('Error fetching users', { error: error.message });

      const enhancedError = new Error(`Failed to fetch users: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        tenantId: userData.organizationId,
        userId: userData.userId,
        endpoint: TOKEN_USAGE_ENDPOINTS.users
      };

      throw enhancedError;
    }
  },

  /**
   * Get detailed breakdown for a specific user
   */
  getUserDetails: async (userData, targetUserId, startDate = null, endDate = null) => {
    try {
      if (!targetUserId) {
        throw new Error('Target user ID is required');
      }

      const headers = await buildHeaders(userData);

      const url = TOKEN_USAGE_ENDPOINTS.userDetails(targetUserId);

      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      logger.info('Fetching user details', { url, params, targetUserId });

      const response = await tokenUsageCachedAxios.get(url, { headers, params });

      logger.debug('User details received', { targetUserId });

      return response.data;
    } catch (error) {
      logger.error('Error fetching user details', { error: error.message, targetUserId });

      const enhancedError = new Error(`Failed to fetch user details: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        tenantId: userData.organizationId,
        userId: userData.userId,
        targetUserId,
        endpoint: 'userDetails'
      };

      throw enhancedError;
    }
  },

  /**
   * Get conversations for a specific user
   */
  getUserConversations: async (userData, targetUserId, startDate = null, endDate = null, sortBy = 'cost') => {
    try {
      if (!targetUserId) {
        throw new Error('Target user ID is required');
      }

      const headers = await buildHeaders(userData);

      const url = TOKEN_USAGE_ENDPOINTS.userConversations(targetUserId);

      const params = { sortBy };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      logger.info('Fetching user conversations', { url, params, targetUserId });

      const response = await tokenUsageCachedAxios.get(url, { headers, params });

      logger.debug('Conversations received', { conversationCount: response.data?.length });

      return response.data;
    } catch (error) {
      logger.error('Error fetching conversations', { error: error.message, targetUserId });

      const enhancedError = new Error(`Failed to fetch conversations: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        tenantId: userData.organizationId,
        userId: userData.userId,
        targetUserId,
        endpoint: 'userConversations'
      };

      throw enhancedError;
    }
  },

  /**
   * Export token usage data in CSV or JSON format
   */
  exportData: async (userData, startDate = null, endDate = null, format = 'csv') => {
    try {
      const headers = await buildHeaders(userData);

      const params = { format };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      logger.info('Exporting data', { url: TOKEN_USAGE_ENDPOINTS.export, params, format });

      if (format === 'csv') {
        // For CSV, we need to handle it as a blob
        const response = await tokenUsageCachedAxios.get(TOKEN_USAGE_ENDPOINTS.export, {
          headers,
          params,
          responseType: 'blob'
        });

        // Create a download link
        const blob = new Blob([response.data], { type: 'text/csv' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `token-usage-${userData.organizationId}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        logger.info('CSV export completed');

        return { success: true, format: 'csv' };
      } else {
        // For JSON, return the data
        const response = await tokenUsageCachedAxios.get(TOKEN_USAGE_ENDPOINTS.export, { headers, params });

        logger.info('JSON export completed');

        return response.data;
      }
    } catch (error) {
      logger.error('Error exporting data', { error: error.message, format });

      const enhancedError = new Error(`Failed to export data: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        tenantId: userData.organizationId,
        userId: userData.userId,
        format,
        endpoint: TOKEN_USAGE_ENDPOINTS.export
      };

      throw enhancedError;
    }
  }
};